"use client";

import clsx, { ClassValue } from "clsx";

import Link from "next/link";
import { Tooltip } from "flowbite-react";

export type ButtonProps = {
  children?: React.ReactNode;
  label?: React.ReactNode;
  type?: "primary" | "secondary" | "tertiary" | "none";
  compact?: boolean;
  tiny?: boolean;
  wide?: boolean;
  disabled?: boolean;
  restrictedText?: string;
  id?: string;
  href?: string;
  target?: "_blank" | "_self" | "_parent" | "_top";
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  secondaryAction?: () => void;
  iconBefore?: React.ReactNode;
  iconAfter?: React.ReactNode;
  testId?: string;
  className?: ClassValue;
  prefetch?: boolean;
  textAlign?: "left" | "center" | "right";
};

const Button: React.FC<ButtonProps> = ({
  href,
  onClick,
  type = "primary",
  className,
  id,
  testId: dataTestId,
  wide,
  compact,
  disabled,
  tiny,
  restrictedText,
  children,
  iconBefore,
  iconAfter,
  prefetch = true,
  target = "_self",
  secondaryAction,
  textAlign = "center",
}) => {
  if (
    !href &&
    !onClick &&
    process.env.NEXT_PUBLIC_ENVIRONMENT === "development"
  ) {
    throw new Error("You must have an href or onClick prop");
  }

  if (restrictedText) {
    disabled = true;
  }

  const commonProps = {
    id,
    disabled,
    "data-testid": dataTestId,
    className: clsx(
      "rounded-full",
      {
        ["py-2 px-4"]: !compact,
        ["py-1 px-2"]: compact,
        ["cursor-not-allowed"]: disabled,
        ["cursor-pointer"]: !disabled,
        // Primary (Black)
        ["bg-black hover:bg-[#4E4D4D] active:bg-[#4E4D4D] text-white"]:
          !disabled && type === "primary",
        ["bg-black/50 text-white/70"]: disabled && type === "primary",

        // Secondary (Blue)
        ["bg-[#0062E2] hover:bg-[#3D91FF] active:bg-[#3D91FF] text-white"]:
          !disabled && type === "secondary",
        ["bg-[#0062E2]/50 text-white"]: disabled && type === "secondary",

        // Tertiary (White)
        ["bg-white hover:bg-[#C0C0C0] active:bg-[#C0C0C0] text-black"]:
          !disabled && type === "tertiary",
        ["bg-white/70 text-black/50"]: disabled && type === "tertiary",

        // None
        ["m-[2px]"]: type === "none",

        // All
        ["w-full"]: wide,
        ["flex"]: !!href,
        ["inline-block"]: !!onClick,
      },
      className
    ),
  };

  const textAlignClass =
    textAlign === "left"
      ? "text-left"
      : textAlign === "right"
        ? "text-right"
        : "text-center";

  const buttonChildren = (
    <span
      className={clsx(textAlignClass, "m-auto", {
        ["text-sm"]: compact,
        ["text-[10.5px]"]: tiny,
      })}
    >
      {iconBefore && (
        <span className={clsx("mr-2", "text-current")}>{iconBefore}</span>
      )}
      {children}
      {iconAfter && <span className="ml-2">{iconAfter}</span>}
    </span>
  );

  if (onClick) {
    const buttonComponent = (
      <button onClick={onClick} {...commonProps}>
        {buttonChildren}
      </button>
    );

    if (restrictedText) {
      return (
        <div className={clsx({ "[&>div:first-of-type]:w-full": wide })}>
          <Tooltip content={restrictedText}>{buttonComponent}</Tooltip>
        </div>
      );
    }

    return buttonComponent;
  }

  if (href) {
    const linkComponent = (
      <Link
        href={href}
        target={target}
        {...commonProps}
        prefetch={prefetch}
        onClick={secondaryAction ? () => secondaryAction() : undefined}
      >
        {buttonChildren}
      </Link>
    );

    if (restrictedText) {
      return (
        <div className={clsx({ "[&>div:first-of-type]:w-full": wide })}>
          <Tooltip content={restrictedText}>{linkComponent}</Tooltip>
        </div>
      );
    }

    return linkComponent;
  }
};

export default Button;
